import axios from 'axios'

type ApiValidationItem = {
  loc?: Array<string | number>
  msg?: string
  type?: string
}

type ApiErrorPayload = {
  detail?: string | Array<unknown>
  message?: string
  code?: string
  status_code?: number
}

export class ErrorApiAplicacion extends Error {
  code: string | null
  statusCode: number | null
  detail?: string | Array<unknown>

  constructor(
    message: string,
    code: string | null = null,
    statusCode: number | null = null,
    detail?: string | Array<unknown>
  ) {
    super(message)
    this.name = 'ErrorApiAplicacion'
    this.code = code
    this.statusCode = statusCode
    this.detail = detail
  }
}

function extraerMensajeValidacion(detail: Array<unknown> | undefined): string | null {
  if (!Array.isArray(detail) || detail.length === 0) {
    return null
  }

  const primerError = detail[0] as ApiValidationItem | undefined
  if (!primerError?.msg) {
    return null
  }

  const campo = Array.isArray(primerError.loc) ? primerError.loc[primerError.loc.length - 1] : null
  if (typeof campo === 'string' && campo.trim()) {
    return `${campo}: ${primerError.msg}`
  }

  return primerError.msg
}

type ErrorApiResuelto = {
  message: string
  code: string | null
  statusCode: number | null
  detail?: string | Array<unknown>
}

export function resolverErrorApi(error: unknown, mensajeFallback: string): ErrorApiResuelto {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiErrorPayload | undefined
    const statusCode = typeof data?.status_code === 'number' ? data.status_code : error.response?.status ?? null
    let code = typeof data?.code === 'string' ? data.code : null

    if (!code && typeof statusCode === 'number') {
      switch (statusCode) {
        case 400:
          code = 'validation_error'
          break
        case 401:
          code = 'unauthorized'
          break
        case 403:
          code = 'forbidden'
          break
        case 404:
          code = 'not_found'
          break
        case 409:
          code = 'conflict'
          break
        case 422:
          code = 'validation_error'
          break
        case 500:
          code = 'internal_error'
          break
      }
    }

    if (code === 'validation_error') {
      const mensajeValidacion = extraerMensajeValidacion(data?.detail as Array<unknown> | undefined)
      if (mensajeValidacion) {
        return {
          message: mensajeValidacion,
          code,
          statusCode,
          detail: data?.detail,
        }
      }
    }

    if (typeof data?.detail === 'string' && data.detail.trim()) {
      return {
        message: data.detail,
        code,
        statusCode,
        detail: data.detail,
      }
    }

    if (typeof data?.message === 'string' && data.message.trim()) {
      return {
        message: data.message,
        code,
        statusCode,
        detail: data?.detail,
      }
    }

    if (code === 'unauthorized') {
      return { message: 'Tu sesión no es válida o ha expirado', code, statusCode, detail: data?.detail }
    }

    if (code === 'forbidden') {
      return { message: 'No tienes permisos para realizar esta acción', code, statusCode, detail: data?.detail }
    }

    if (code === 'not_found') {
      return { message: 'El recurso solicitado no fue encontrado', code, statusCode, detail: data?.detail }
    }

    return { message: mensajeFallback, code, statusCode, detail: data?.detail }
  }

  if (error instanceof Error && error.message.trim()) {
    return {
      message: error.message,
      code: error instanceof ErrorApiAplicacion ? error.code : null,
      statusCode: error instanceof ErrorApiAplicacion ? error.statusCode : null,
      detail: error instanceof ErrorApiAplicacion ? error.detail : undefined,
    }
  }

  return { message: mensajeFallback, code: null, statusCode: null }
}

export function obtenerMensajeErrorApi(error: unknown, mensajeFallback: string): string {
  return resolverErrorApi(error, mensajeFallback).message
}

export function obtenerCodigoErrorApi(error: unknown): string | null {
  return resolverErrorApi(error, '').code
}

export function crearErrorAplicacionApi(error: unknown, mensajeFallback: string): ErrorApiAplicacion {
  const resuelto = resolverErrorApi(error, mensajeFallback)
  return new ErrorApiAplicacion(
    resuelto.message,
    resuelto.code,
    resuelto.statusCode,
    resuelto.detail
  )
}

export function esErrorApiAplicacion(error: unknown): error is ErrorApiAplicacion {
  return error instanceof ErrorApiAplicacion
}

type OpcionesPresentacionErrorApi = {
  tituloFallback: string
  mensajeFallback: string
  titulosPorCodigo?: Partial<Record<string, string>>
  mensajesPorCodigo?: Partial<Record<string, string>>
}

type PresentacionErrorApi = {
  title: string
  message: string
  code: string | null
  statusCode: number | null
}

export function resolverPresentacionErrorApi(
  error: unknown,
  opciones: OpcionesPresentacionErrorApi
): PresentacionErrorApi {
  const resuelto = resolverErrorApi(error, opciones.mensajeFallback)

  const tituloBasePorCodigo: Partial<Record<string, string>> = {
    validation_error: 'Datos inválidos',
    unauthorized: 'Sesión no válida',
    forbidden: 'Acceso denegado',
    not_found: 'Recurso no encontrado',
    conflict: 'Conflicto de datos',
  }

  return {
    title:
      (resuelto.code && opciones.titulosPorCodigo?.[resuelto.code]) ||
      (resuelto.code && tituloBasePorCodigo[resuelto.code]) ||
      opciones.tituloFallback,
    message:
      (resuelto.code && opciones.mensajesPorCodigo?.[resuelto.code]) ||
      resuelto.message ||
      opciones.mensajeFallback,
    code: resuelto.code,
    statusCode: resuelto.statusCode,
  }
}
